import React, { useState, useEffect } from 'react';
import { tenantService, complianceService, agreementService } from '../../services/api';

interface Property {
    id: string;
    address: string;
    postcode?: string;
    type?: string;
}

interface NewTenancyWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialPropertyId?: string;
}

type WizardStep = 'PROPERTY' | 'TENANT' | 'TERMS' | 'RENT_PAYMENT_DATE' | 'DEPOSIT' | 'COMPLIANCE' | 'AGREEMENT' | 'REVIEW';

export default function NewTenancyWizard({ isOpen, onClose, onSuccess, initialPropertyId }: NewTenancyWizardProps) {
    const [step, setStep] = useState<WizardStep>('PROPERTY');
    const [properties, setProperties] = useState<Property[]>([]);
    const [agreements, setAgreements] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

    // Form Data
    const [formData, setFormData] = useState({
        propertyId: initialPropertyId || '',
        name: '',
        email: '',
        phone: '',
        tenancyStartDate: '',
        tenancyEndDate: '',
        rentAmount: '',
        rentFrequency: 'monthly',
        depositAmount: '',
        depositScheme: '',
        depositReference: '',
        complianceException: false,
        complianceExceptionReason: '',
        complianceExceptionRemediationDate: '',
        agreementId: '',
        rentPaymentDay: '',
        freePeriodGranted: false,
        freePeriodDays: '',
    });

    // Capacity check state — populated when a property is selected on step 1
    const [capacityState, setCapacityState] = useState<{
        existingCount: number;
        maxCapacity: number;
        type: string;
        isResidential: boolean;
        isFullyOccupied: boolean;
    } | null>(null);

    // Multi-tenant state
    const [additionalTenants, setAdditionalTenants] = useState<Array<{ name: string; email: string; phone: string }>>([]);

    // Compliance State
    const [complianceResult, setComplianceResult] = useState<any>(null);

    // 1. Fetch properties on mount
    useEffect(() => {
        if (isOpen) {
            fetchProperties();
            fetchAgreements();
            if (initialPropertyId) {
                setFormData(prev => ({ ...prev, propertyId: initialPropertyId }));
                fetchPropertyDetails(initialPropertyId);
            }
        }
    }, [isOpen, initialPropertyId]);

    const fetchPropertyDetails = async (propertyId: string) => {
        try {
            const [propRes, tenantsRes] = await Promise.all([
                complianceService.getProperty(propertyId),
                tenantService.getTenants().catch(() => ({ data: { data: [] } }))
            ]);
            const propertyData = propRes.data.data || propRes.data;
            const prop = properties.find(p => p.id === propertyId) || propertyData;
            const type = (prop.type || propertyData?.type || 'Residential').toString();
            const isResidential = !/hmo/i.test(type);

            setSelectedProperty({
                id: prop.id,
                address: typeof prop.address === 'object' ? prop.address.line1 : prop.address,
                postcode: typeof prop.address === 'object' ? prop.address.postcode : prop.postcode,
                type
            });

            // Capacity check — count active tenants on this property
            const allTenants: any[] = tenantsRes.data?.data || tenantsRes.data || [];
            const existingCount = allTenants.filter(
                t => t.propertyId === propertyId && (t.status || 'active') === 'active'
            ).length;

            // Capacity: Residential = 1 lead tenant; HMO = bedrooms (or fall back to 6)
            const maxCapacity = isResidential
                ? 1
                : (parseInt(propertyData?.bedrooms || prop.bedrooms || '0', 10) || 6);

            setCapacityState({
                existingCount,
                maxCapacity,
                type,
                isResidential,
                isFullyOccupied: existingCount >= maxCapacity,
            });
        } catch (err) {
            console.error('Failed to fetch property details:', err);
        }
    };

    const fetchProperties = async () => {
        try {
            const res = await complianceService.getProperties();
            const data = res.data?.data || res.data || [];
            setProperties(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch properties:', err);
        }
    };

    const fetchAgreements = async () => {
        try {
            const res = await agreementService.list();
            setAgreements(res.data?.data || []);
        } catch (err) {
            console.error('Failed to fetch agreements:', err);
        }
    };

    // 2. Compliance Check Logic
    const runComplianceCheck = async (propId: string) => {
        if (!propId) return;
        setChecking(true);
        setError(null);
        try {
            const res = await tenantService.checkCompliance(propId, {
                rentAmount: parseFloat(formData.rentAmount) || 0,
                rentFrequency: formData.rentFrequency,
                depositAmount: parseFloat(formData.depositAmount) || 0
            });
            setComplianceResult(res.data.data);
            return res.data.data;
        } catch (err: any) {
            console.error('Compliance check failed:', err);
            setError('Failed to run compliance check. Please try again.');
        } finally {
            setChecking(false);
        }
    };

    // 3. Step Navigation Handlers
    const handleNext = async () => {
        setError(null);

        if (step === 'PROPERTY') {
            if (!formData.propertyId) {
                setError('Please select a property to continue.');
                return;
            }
            // Block if the property is already full (Residential = 1, HMO = bedrooms)
            if (capacityState?.isFullyOccupied) {
                setError(
                    capacityState.isResidential
                        ? `This residential property already has a tenant. Single-occupancy properties cannot accept additional tenants.`
                        : `This HMO is fully occupied (${capacityState.existingCount} / ${capacityState.maxCapacity}). End an existing tenancy before adding a new one.`
                );
                return;
            }
            setStep('TENANT');
            return;
        }

        if (step === 'TENANT') {
            if (!formData.name || !formData.email) { setError('Name and Email are required'); return; }

            // Validate additional tenants
            const hasInvalidAdditional = additionalTenants.some(t => !t.name || !t.email);
            if (hasInvalidAdditional) {
                setError('All tenant names and emails are required');
                return;
            }

            // Check for duplicate emails
            const emails = [formData.email, ...additionalTenants.map(t => t.email)];
            const uniqueEmails = new Set(emails);
            if (emails.length !== uniqueEmails.size) {
                setError('Duplicate emails detected');
                return;
            }

            setStep('TERMS');
            return;
        }

        if (step === 'TERMS') {
            if (!formData.tenancyStartDate || !formData.rentAmount) { setError('Start Date and Rent Amount are required'); return; }
            // Auto-set rentPaymentDay to the start date day if not already set
            if (!formData.rentPaymentDay && formData.tenancyStartDate) {
                const startDay = new Date(formData.tenancyStartDate).getDate();
                setFormData(prev => ({ ...prev, rentPaymentDay: String(startDay) }));
            }
            setStep('RENT_PAYMENT_DATE');
            return;
        }

        if (step === 'RENT_PAYMENT_DATE') {
            if (!formData.rentPaymentDay) { setError('Rent payment day is required'); return; }
            const day = parseInt(formData.rentPaymentDay, 10);
            if (day < 1 || day > 28) { setError('Rent payment day must be between 1 and 28'); return; }
            setStep('DEPOSIT');
            return;
        }

        if (step === 'DEPOSIT') {
            // Run live check on compliance
            const result = await runComplianceCheck(formData.propertyId);
            if (!result) return;

            const depositBlocker = result.blockers?.find((b: any) => b.code === 'DEPOSIT_EXCEEDS_CAP');
            if (depositBlocker) {
                setError(depositBlocker.message);
                return;
            }

            setStep('COMPLIANCE');
            return;
        }

        if (step === 'COMPLIANCE') {
            const blocked = complianceResult && !complianceResult.canProceed;
            if (blocked) {
                if (!formData.complianceException) {
                    setError('You cannot proceed until all compliance issues are resolved, or check the Exception box.');
                    return;
                }
                if (!formData.complianceExceptionReason?.trim()) {
                    setError('Officer comment is required when applying a legal exception.');
                    return;
                }
                if (!formData.complianceExceptionRemediationDate) {
                    setError('Date of remediation is required when applying a legal exception.');
                    return;
                }
                const remediation = new Date(formData.complianceExceptionRemediationDate);
                const today = new Date(); today.setHours(0, 0, 0, 0);
                if (remediation < today) {
                    setError('Date of remediation must be today or later.');
                    return;
                }
            }
            setStep('AGREEMENT');
            return;
        }

        if (step === 'AGREEMENT') {
            // Agreement is optional for now, but good to have selected if available
            setStep('REVIEW');
            return;
        }
    };

    const handleBack = () => {
        if (step === 'TENANT') setStep('PROPERTY');
        else if (step === 'TERMS') setStep('TENANT');
        else if (step === 'RENT_PAYMENT_DATE') setStep('TERMS');
        else if (step === 'DEPOSIT') setStep('RENT_PAYMENT_DATE');
        else if (step === 'COMPLIANCE') setStep('DEPOSIT');
        else if (step === 'AGREEMENT') setStep('COMPLIANCE');
        else if (step === 'REVIEW') setStep('AGREEMENT');
    };

    // After a successful submit we surface a "Pending Activation" step
    // confirming the tenancy is created but not yet active — the officer
    // must click Request Acknowledgement to send the required docs, and
    // the tenancy only becomes ACTIVE once the tenant has confirmed
    // receipt via the portal.
    const [pendingActivation, setPendingActivation] = useState<{
        tenantName: string;
        tenantEmail: string;
        propertyId: string;
    } | null>(null);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await tenantService.addTenant({
                ...formData,
                rentAmount: parseFloat(formData.rentAmount),
                depositAmount: parseFloat(formData.depositAmount) || 0,
                rentPaymentDay: formData.rentPaymentDay ? parseInt(formData.rentPaymentDay, 10) : undefined,
                freePeriodGranted: formData.freePeriodGranted,
                freePeriodDays: formData.freePeriodDays ? parseInt(formData.freePeriodDays, 10) : undefined,
                additionalTenants,
                isJointTenancy: additionalTenants.length > 0,
                // Right to Rent — auto-attach the gov.uk code of practice for
                // every tenant on this tenancy (lead + additional). Backend
                // stores the URL against the tenant's portal so it appears in
                // their document pack alongside How to Rent / Gas Safety / etc.
                autoAttachRightToRent: true,
                rightToRentUrl: 'https://www.gov.uk/government/publications/right-to-rent-document-checks-a-user-guide',
                // Agreement merge data — backend renders the template with
                // these fields substituted into placeholders ({{tenantName}},
                // {{propertyAddress}}, {{rentAmount}}, etc.) when filing the
                // signed copy in the Court Evidence Pack.
                agreementMergeFields: formData.agreementId ? {
                    propertyAddress: (() => {
                        const p = properties.find(x => x.id === formData.propertyId);
                        const line1 = p ? ((p.address as any)?.line1 || p.address) : '';
                        const postcode = p ? ((p.address as any)?.postcode || p.postcode || '') : '';
                        return [line1, postcode].filter(Boolean).join(', ');
                    })(),
                    leadTenantName: formData.name,
                    leadTenantEmail: formData.email,
                    leadTenantPhone: formData.phone,
                    additionalTenants: additionalTenants.map(t => ({ name: t.name, email: t.email, phone: t.phone })),
                    tenancyStartDate: formData.tenancyStartDate,
                    tenancyEndDate: formData.tenancyEndDate,
                    rentAmount: parseFloat(formData.rentAmount) || 0,
                    rentFrequency: formData.rentFrequency,
                    depositAmount: parseFloat(formData.depositAmount) || 0,
                    depositScheme: formData.depositScheme,
                    depositReference: formData.depositReference,
                } : undefined,
                // Compliance exception audit fields (only set when the
                // officer ticked the box at the COMPLIANCE step)
                ...(formData.complianceException ? {
                    complianceExceptionReason: formData.complianceExceptionReason,
                    complianceExceptionRemediationDate: formData.complianceExceptionRemediationDate,
                } : {})
            });
            // Show the pending-activation CTA before closing. onSuccess
            // still refreshes the list in the background so the new
            // tenancy appears with its "Pending Activation" badge.
            onSuccess();
            setPendingActivation({
                tenantName: formData.name,
                tenantEmail: formData.email,
                propertyId: formData.propertyId,
            });
        } catch (err: any) {
            console.error('Submission error:', err);
            // Show specific blocker message if API returns 422
            if (err.response?.status === 422 && err.response?.data?.blockers) {
                const blockers = err.response.data.blockers.map((b: any) => b.message).join('\n');
                setError(`Compliance Check Failed:\n${blockers}`);
            } else {
                setError(err.response?.data?.message || 'Failed to create tenancy');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Render Helpers
    const renderStepIndicator = () => {
        const labels = ['Property', 'Tenant', 'Terms', 'Rent Payment', 'Deposit', 'Compliance', 'Agreement', 'Review'];
        const stepNames: WizardStep[] = ['PROPERTY', 'TENANT', 'TERMS', 'RENT_PAYMENT_DATE', 'DEPOSIT', 'COMPLIANCE', 'AGREEMENT', 'REVIEW'];
        const currentIdx = stepNames.indexOf(step);

        return (
            <div className="flex items-center justify-center mb-10 overflow-x-auto pb-4">
                {labels.map((label, idx) => {
                    const isCompleted = idx < currentIdx;
                    const isCurrent = idx === currentIdx;

                    return (
                        <React.Fragment key={label}>
                            <div className="flex flex-col items-center min-w-[3rem]">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mb-2 transition-all duration-300
                                    ${isCompleted ? 'bg-emerald-600 text-white shadow-sm' :
                                        isCurrent ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500 scale-110' : 'bg-gray-100 text-gray-400'}`}>
                                    {isCompleted ? <i className="fas fa-check"></i> : idx + 1}
                                </div>
                                <span className={`text-[10px] uppercase tracking-wider text-center whitespace-nowrap ${isCurrent ? 'font-bold text-emerald-700' : 'text-gray-400 font-medium'}`}>{label}</span>
                            </div>
                            {idx < labels.length - 1 && (
                                <div className={`h-[2px] w-6 sm:w-10 mx-1 mb-6 transition-colors duration-300 ${isCompleted ? 'bg-emerald-600' : 'bg-gray-100'}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // Pending-activation confirmation — shown right after a successful
    // tenancy create. Officer either lands on the property page to send
    // the acknowledgement bundle now, or closes and does it later.
    if (pendingActivation) {
        const goRequestAck = () => {
            // Navigate to the property detail page with a hash so the
            // PropertyDetail page can auto-open the Request Ack modal.
            window.location.href = `/properties/${pendingActivation.propertyId}#request-acknowledgement`;
        };
        const closePending = () => {
            setPendingActivation(null);
            onClose();
        };
        return (
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={closePending}></div>
                    <div className="relative inline-block w-full max-w-lg p-8 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl">
                        <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-amber-100 mb-4">
                            <i className="fas fa-hourglass-half text-2xl text-amber-600"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Tenancy created — pending activation</h2>
                        <p className="text-sm text-gray-600 text-center mb-6">
                            <span className="font-semibold text-gray-800">{pendingActivation.tenantName}</span> has been added in
                            <span className="font-semibold text-amber-700"> Pending Activation</span>.
                            The tenancy becomes fully active once the tenant acknowledges receipt of the required documents via the portal.
                        </p>
                        <ol className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 text-sm text-amber-900 space-y-2">
                            <li className="flex gap-2"><i className="fas fa-check-circle text-amber-600 mt-0.5"></i>Tenancy record created</li>
                            <li className="flex gap-2"><i className="fas fa-paper-plane text-amber-600 mt-0.5"></i>Click <span className="font-bold">Request Acknowledgement</span> to send all required documents to <span className="font-mono text-[12px]">{pendingActivation.tenantEmail}</span></li>
                            <li className="flex gap-2"><i className="fas fa-user-check text-amber-600 mt-0.5"></i>Tenant confirms via portal → tenancy auto-activates</li>
                        </ol>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={closePending}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                            >
                                Do it later
                            </button>
                            <button
                                onClick={goRequestAck}
                                className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 inline-flex items-center justify-center gap-2"
                            >
                                <i className="fas fa-paper-plane"></i>
                                Request Acknowledgement
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>

                <div className="relative inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl">
                    <div className="flex justify-between mb-4">
                        <h2 className="text-2xl font-bold text-gray-900">Tenancy Wizard</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><i className="fas fa-times"></i></button>
                    </div>

                    {renderStepIndicator()}

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 whitespace-pre-wrap">
                            <i className="fas fa-exclamation-circle mr-2"></i>{error}
                        </div>
                    )}

                    <div className="min-h-[400px]">
                        {step === 'PROPERTY' && (
                            <div className="space-y-6 animate-in fade-in duration-500">
                                <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100 mb-6 font-medium text-emerald-800 flex items-center shadow-sm">
                                    <i className="fas fa-home text-2xl mr-4 text-emerald-600"></i>
                                    Step 1: Select Property
                                </div>
                                <select
                                    className="w-full p-4 border-2 border-gray-100 rounded-xl focus:ring-2 focus:ring-emerald-500 appearance-none bg-white shadow-sm font-bold text-gray-700"
                                    value={formData.propertyId}
                                    onChange={(e) => {
                                        setFormData({ ...formData, propertyId: e.target.value });
                                        setCapacityState(null);
                                        setError(null);
                                        if (e.target.value) fetchPropertyDetails(e.target.value);
                                    }}
                                >
                                    <option value="">-- Choose from your portfolio --</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{(p.address as any)?.line1 || p.address} ({(p.address as any)?.postcode || ''})</option>
                                    ))}
                                </select>

                                {capacityState && (
                                    capacityState.isFullyOccupied ? (
                                        <div className="p-5 bg-red-50 border-2 border-red-200 rounded-2xl flex items-start">
                                            <i className="fas fa-ban text-2xl text-red-600 mr-4 mt-1"></i>
                                            <div>
                                                <p className="font-bold text-red-900 uppercase text-sm tracking-wider">Property Fully Occupied</p>
                                                <p className="text-sm text-red-800 mt-1">
                                                    {capacityState.isResidential
                                                        ? `Single-dwelling residential property with 1 active tenant. You cannot add another tenant unless you end the existing tenancy first.`
                                                        : `HMO at capacity (${capacityState.existingCount} / ${capacityState.maxCapacity} rooms occupied). End an existing tenancy before adding a new one.`}
                                                </p>
                                            </div>
                                        </div>
                                    ) : capacityState.existingCount > 0 ? (
                                        <div className="p-5 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-start">
                                            <i className="fas fa-info-circle text-2xl text-amber-600 mr-4 mt-1"></i>
                                            <div>
                                                <p className="font-bold text-amber-900 uppercase text-sm tracking-wider">{capacityState.type} — Partially Occupied</p>
                                                <p className="text-sm text-amber-800 mt-1">
                                                    {capacityState.existingCount} of {capacityState.maxCapacity} tenant(s) currently registered. Adding 1 more is allowed.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-5 bg-emerald-50 border-2 border-emerald-200 rounded-2xl flex items-start">
                                            <i className="fas fa-check-circle text-2xl text-emerald-600 mr-4 mt-1"></i>
                                            <div>
                                                <p className="font-bold text-emerald-900 uppercase text-sm tracking-wider">{capacityState.type} — Vacant</p>
                                                <p className="text-sm text-emerald-800 mt-1">
                                                    No active tenants. Capacity: {capacityState.maxCapacity}.
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {step === 'TENANT' && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                <div className="flex justify-between items-center border-b pb-2 mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Step 2: Tenant Details
                                    </h3>
                                    <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                                        {selectedProperty?.type || 'Property'} • Joint Tenancy Enabled
                                    </span>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-700">Lead Tenant / Tenant 1</h4>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="col-span-2">
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Full Name *</label>
                                            <input type="text" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="e.g. David Smith" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Email *</label>
                                            <input type="email" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="david@example.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Phone</label>
                                            <input type="tel" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="+44 ..." value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-sm font-medium text-gray-700">Joint Tenants</h4>
                                        <button
                                            type="button"
                                            onClick={() => setAdditionalTenants([...additionalTenants, { name: '', email: '', phone: '' }])}
                                            className="inline-flex items-center px-3 py-1 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                                        >
                                            <i className="fas fa-plus mr-2"></i>
                                            Add Joint Tenant
                                        </button>
                                    </div>
                                    {additionalTenants.length > 0 ? (
                                        <div className="space-y-4">
                                            {additionalTenants.map((tenant, idx) => (
                                                <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <h5 className="text-sm font-medium text-gray-600">Tenant {idx + 2}</h5>
                                                        <button
                                                            type="button"
                                                            onClick={() => setAdditionalTenants(additionalTenants.filter((_, i) => i !== idx))}
                                                            className="text-red-500 hover:text-red-700 flex items-center text-xs font-semibold"
                                                        >
                                                            <i className="fas fa-trash mr-1"></i> Remove Joint Tenant
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="col-span-2">
                                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Full Name *</label>
                                                            <input
                                                                type="text"
                                                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                                                                placeholder="Tenant Name"
                                                                value={tenant.name}
                                                                onChange={e => {
                                                                    const updated = [...additionalTenants];
                                                                    updated[idx].name = e.target.value;
                                                                    setAdditionalTenants(updated);
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Email *</label>
                                                            <input
                                                                type="email"
                                                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                                                                placeholder="email@example.com"
                                                                value={tenant.email}
                                                                onChange={e => {
                                                                    const updated = [...additionalTenants];
                                                                    updated[idx].email = e.target.value;
                                                                    setAdditionalTenants(updated);
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Phone</label>
                                                            <input
                                                                type="tel"
                                                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                                                                placeholder="+44 ..."
                                                                value={tenant.phone}
                                                                onChange={e => {
                                                                    const updated = [...additionalTenants];
                                                                    updated[idx].phone = e.target.value;
                                                                    setAdditionalTenants(updated);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">No joint tenants. Click "Add Joint Tenant" to add more.</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 'TERMS' && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                <h3 className="text-xl font-bold text-gray-900 border-b pb-2 mb-6">Step 3: Terms</h3>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Start Date *</label>
                                        <input type="date" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.tenancyStartDate} onChange={e => setFormData({ ...formData, tenancyStartDate: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Rent Amount *</label>
                                        <input type="number" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.rentAmount} onChange={e => setFormData({ ...formData, rentAmount: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Frequency</label>
                                        <select className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.rentFrequency} onChange={e => setFormData({ ...formData, rentFrequency: e.target.value })}>
                                            <option value="monthly">Monthly</option>
                                            <option value="weekly">Weekly</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Target End Date</label>
                                        <input type="date" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.tenancyEndDate} onChange={e => setFormData({ ...formData, tenancyEndDate: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 'RENT_PAYMENT_DATE' && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                <h3 className="text-xl font-bold text-gray-900 border-b pb-2 mb-6">Step 4: Rent Payment Date</h3>

                                <div className="p-5 bg-blue-50 border border-blue-100 rounded-2xl flex items-start">
                                    <i className="fas fa-calendar-alt text-2xl text-blue-600 mr-4 mt-1"></i>
                                    <div className="flex-1">
                                        <p className="font-bold text-blue-900">When is rent due each month?</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            The rent payment date is the day of the month the tenant is required to pay rent.
                                            This defaults to the tenancy start date but can be changed if your tenant prefers
                                            a different payment day (e.g. payday).
                                        </p>
                                        <p className="text-xs text-blue-600 mt-2">
                                            If the payment date differs from the tenancy start date, an amber information
                                            banner will appear on the tenancy detail page for your reference.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Payment Day of Month *</label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="number"
                                                min="1"
                                                max="28"
                                                className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500"
                                                placeholder="e.g. 1"
                                                value={formData.rentPaymentDay}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFormData({ ...formData, rentPaymentDay: val });
                                                }}
                                            />
                                            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">of each month</span>
                                        </div>
                                        {formData.tenancyStartDate && (
                                            <p className="text-xs text-gray-400 mt-1">
                                                <i className="fas fa-info-circle mr-1"></i>
                                                Start date is the {new Date(formData.tenancyStartDate).getDate()}{['st','nd','rd','th'][Math.min((new Date(formData.tenancyStartDate).getDate() % 10) - 1, 3)] || 'th'} — payment day auto-set to match
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="border-t pt-6 mt-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <input
                                            type="checkbox"
                                            id="freePeriodGranted"
                                            checked={formData.freePeriodGranted}
                                            onChange={e => setFormData({ ...formData, freePeriodGranted: e.target.checked })}
                                            className="h-5 w-5 text-emerald-600 rounded"
                                        />
                                        <label htmlFor="freePeriodGranted" className="text-sm font-medium text-gray-700">
                                            Grant a free period / rent-free days
                                        </label>
                                    </div>
                                    {formData.freePeriodGranted && (
                                        <div className="ml-8 p-4 bg-amber-50 border border-amber-100 rounded-xl animate-in fade-in duration-300">
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Number of free days</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="90"
                                                className="w-full p-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                                                placeholder="e.g. 7"
                                                value={formData.freePeriodDays}
                                                onChange={e => setFormData({ ...formData, freePeriodDays: e.target.value })}
                                            />
                                            <p className="text-xs text-amber-700 mt-1">
                                                <i className="fas fa-clock mr-1"></i>
                                                Rent collection will be delayed by this many days from the start date.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 'DEPOSIT' && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                <h3 className="text-xl font-bold text-gray-900 border-b pb-2 mb-6">Step 5: Deposit</h3>
                                <div className="grid grid-cols-3 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Amount</label>
                                        <input type="number" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.depositAmount} onChange={e => setFormData({ ...formData, depositAmount: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Scheme</label>
                                        <select className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.depositScheme} onChange={e => setFormData({ ...formData, depositScheme: e.target.value })}>
                                            <option value="">-- select --</option>
                                            <option value="DPS">DPS</option>
                                            <option value="TDS">TDS</option>
                                            <option value="MyDeposits">MyDeposits</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Ref</label>
                                        <input type="text" className="w-full p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500" value={formData.depositReference} onChange={e => setFormData({ ...formData, depositReference: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 'COMPLIANCE' && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                <h3 className="text-xl font-bold text-gray-900 border-b pb-2 mb-6">Step 6: Compliance</h3>

                                {/* Right to Rent — auto-attached for every tenant
                                    in this tenancy. The gov.uk code of practice
                                    is the legally-required document for the
                                    landlord's Right to Rent check (Immigration
                                    Act 2014). Attached here so it lands in the
                                    tenant portal automatically on creation. */}
                                <div className="p-5 bg-indigo-50 border border-indigo-100 rounded-2xl">
                                    <div className="flex items-start">
                                        <i className="fas fa-passport text-2xl text-indigo-600 mr-4 mt-1"></i>
                                        <div className="flex-1">
                                            <p className="font-bold text-indigo-900">Right to Rent — auto-attached</p>
                                            <p className="text-xs text-indigo-700 mt-1">
                                                The gov.uk Right to Rent code of practice will be auto-attached and
                                                served via the tenant portal for each of the following tenants:
                                            </p>
                                            <ul className="mt-3 space-y-1 text-sm text-indigo-900 font-medium">
                                                <li className="flex items-center">
                                                    <i className="fas fa-user-check text-emerald-600 mr-2 text-xs"></i>
                                                    {formData.name || 'Lead tenant'} {formData.email && <span className="text-indigo-500 ml-1">({formData.email})</span>}
                                                </li>
                                                {additionalTenants.map((t, i) => (
                                                    <li key={i} className="flex items-center">
                                                        <i className="fas fa-user-check text-emerald-600 mr-2 text-xs"></i>
                                                        {t.name || `Co-tenant ${i + 1}`} {t.email && <span className="text-indigo-500 ml-1">({t.email})</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                            <a
                                                href="https://www.gov.uk/government/publications/right-to-rent-document-checks-a-user-guide"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center mt-3 text-xs font-bold text-indigo-700 hover:text-indigo-900 underline"
                                            >
                                                <i className="fas fa-external-link-alt mr-1.5"></i>
                                                View gov.uk source
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {checking ? <p className="animate-pulse text-emerald-600">Verifying documents...</p> : (
                                    <div className="space-y-4">
                                        {complianceResult?.canProceed ? (
                                            <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center">
                                                <i className="fas fa-check-circle text-3xl text-emerald-600 mr-4"></i>
                                                <p className="font-bold text-emerald-900">Compliant & Legal</p>
                                            </div>
                                        ) : (
                                            <div className="p-6 bg-red-50 border border-red-100 rounded-2xl">
                                                <h4 className="font-bold text-red-900 mb-2 flex items-center">
                                                    <i className="fas fa-exclamation-octagon mr-2"></i> Compliance Blockers Found
                                                </h4>
                                                <ul className="space-y-2 mb-6">
                                                    {complianceResult?.blockers?.map((b: any, i: number) => (
                                                        <li key={i} className="text-sm text-red-700 font-medium font-mono">[{b.code}] {b.message}</li>
                                                    ))}
                                                </ul>
                                                <div className="space-y-3">
                                                    <div className="flex items-center bg-white p-4 rounded-xl border border-red-200 shadow-sm">
                                                        <input
                                                            type="checkbox"
                                                            id="exc"
                                                            checked={formData.complianceException}
                                                            onChange={e => setFormData({
                                                                ...formData,
                                                                complianceException: e.target.checked,
                                                                // Reset comment + date if unchecked
                                                                complianceExceptionReason: e.target.checked ? formData.complianceExceptionReason : '',
                                                                complianceExceptionRemediationDate: e.target.checked ? formData.complianceExceptionRemediationDate : '',
                                                            })}
                                                            className="h-5 w-5 text-emerald-600 mr-4"
                                                        />
                                                        <label htmlFor="exc" className="text-sm font-bold text-gray-800">I have a legal exception to proceed</label>
                                                    </div>

                                                    {formData.complianceException && (
                                                        <div className="bg-white p-5 rounded-xl border border-red-200 shadow-sm space-y-4 animate-in fade-in duration-300">
                                                            <div>
                                                                <label className="block text-xs font-black uppercase tracking-wider text-gray-600 mb-2">
                                                                    Officer's Comment <span className="text-red-600">*</span>
                                                                </label>
                                                                <textarea
                                                                    rows={3}
                                                                    placeholder="Describe the legal basis for the exception and the steps that will be taken to bring the tenancy into compliance..."
                                                                    value={formData.complianceExceptionReason}
                                                                    onChange={e => setFormData({ ...formData, complianceExceptionReason: e.target.value })}
                                                                    className="w-full p-3 border-2 border-gray-100 rounded-xl focus:ring-2 focus:ring-red-300 focus:border-red-300 text-sm"
                                                                />
                                                                <p className="text-[10px] text-gray-500 mt-1">Logged in the immutable audit trail with your officer ID.</p>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-black uppercase tracking-wider text-gray-600 mb-2">
                                                                    Date of Remediation <span className="text-red-600">*</span>
                                                                </label>
                                                                <input
                                                                    type="date"
                                                                    min={new Date().toISOString().split('T')[0]}
                                                                    value={formData.complianceExceptionRemediationDate}
                                                                    onChange={e => setFormData({ ...formData, complianceExceptionRemediationDate: e.target.value })}
                                                                    className="w-full p-3 border-2 border-gray-100 rounded-xl focus:ring-2 focus:ring-red-300 focus:border-red-300 text-sm"
                                                                />
                                                                <p className="text-[10px] text-gray-500 mt-1">All blockers must be cleared by this date.</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {step === 'AGREEMENT' && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                <h3 className="text-xl font-bold text-gray-900 border-b pb-2 mb-6">Step 7: Agreement</h3>
                                <div className="p-6 border-2 border-dashed border-gray-100 rounded-3xl bg-gray-50">
                                    <select
                                        className="w-full p-4 bg-white border-2 border-gray-100 rounded-2xl focus:ring-2 focus:ring-emerald-500 shadow-sm font-medium"
                                        value={formData.agreementId}
                                        onChange={e => setFormData({ ...formData, agreementId: e.target.value })}
                                    >
                                        <option value="">-- Choose Template --</option>
                                        {agreements.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-3 italic">Linking an agreement includes it in the automated Court Evidence Pack. The fields below are auto-appended from Steps 1–5.</p>
                                </div>

                                {/* Auto-populated agreement fields preview.
                                    Pulled from Steps 1-5 so the landlord can
                                    see exactly what will be merged into the
                                    template (tenant names, property address,
                                    dates, rent, deposit). All values are
                                    editable upstream — change a step to
                                    update this preview. */}
                                {formData.agreementId && (() => {
                                    const prop = properties.find(p => p.id === formData.propertyId);
                                    const addr = prop ? ((prop.address as any)?.line1 || prop.address) : '';
                                    const postcode = prop ? ((prop.address as any)?.postcode || prop.postcode || '') : '';
                                    const allTenants = [
                                        { name: formData.name, email: formData.email, phone: formData.phone },
                                        ...additionalTenants,
                                    ].filter(t => t.name);
                                    return (
                                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                                            <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
                                                <h4 className="text-sm font-bold text-emerald-900 uppercase tracking-wider">
                                                    <i className="fas fa-file-signature mr-2"></i>
                                                    Auto-filled agreement fields
                                                </h4>
                                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">From Steps 1-5</span>
                                            </div>
                                            <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Property Address</p>
                                                    <p className="font-medium text-gray-900">{addr || '—'}</p>
                                                    <p className="text-xs text-gray-500">{postcode}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Tenancy Term</p>
                                                    <p className="font-medium text-gray-900">{formData.tenancyStartDate ? new Date(formData.tenancyStartDate).toLocaleDateString() : '—'}</p>
                                                    <p className="text-xs text-gray-500">to {formData.tenancyEndDate ? new Date(formData.tenancyEndDate).toLocaleDateString() : 'Rolling'}</p>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                                                        Tenant{allTenants.length > 1 ? 's' : ''} ({allTenants.length})
                                                    </p>
                                                    <ul className="space-y-1">
                                                        {allTenants.map((t, i) => (
                                                            <li key={i} className="font-medium text-gray-900 text-sm">
                                                                {i === 0 && <span className="text-[9px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded mr-2 uppercase tracking-wider">Lead</span>}
                                                                {t.name}
                                                                {t.email && <span className="text-gray-500 ml-2">· {t.email}</span>}
                                                                {t.phone && <span className="text-gray-500 ml-2">· {t.phone}</span>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Rent</p>
                                                    <p className="font-medium text-gray-900">£{formData.rentAmount || '0'} <span className="text-xs text-gray-500">/ {formData.rentFrequency}</span></p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Deposit</p>
                                                    <p className="font-medium text-gray-900">£{formData.depositAmount || '0'}</p>
                                                    <p className="text-xs text-gray-500">{formData.depositScheme || 'No scheme'}{formData.depositReference ? ` · ref ${formData.depositReference}` : ''}</p>
                                                </div>
                                            </div>
                                            <div className="px-5 py-3 bg-gray-50 border-t text-[11px] text-gray-500">
                                                <i className="fas fa-info-circle mr-1.5"></i>
                                                These values are merged into the template at creation. Go back to any earlier step to change them.
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {step === 'REVIEW' && (
                            <div className="animate-in zoom-in-95 duration-500">
                                <div className="bg-gray-900 text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 opacity-10">
                                        <i className="fas fa-file-signature text-9xl"></i>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-8 flex items-center">
                                        <i className="fas fa-rocket mr-3 text-emerald-400"></i>
                                        Final Tenancy Review
                                    </h3>

                                    <div className="grid grid-cols-2 gap-x-12 gap-y-8 text-sm">
                                        <div className="border-l-2 border-emerald-500 pl-4">
                                            <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Property</p>
                                            <p className="font-medium text-lg truncate">{(properties.find(p => p.id === formData.propertyId)?.address as any)?.line1 || 'Selected Property'}</p>
                                            <p className="text-gray-400 text-xs">{(properties.find(p => p.id === formData.propertyId)?.address as any)?.postcode || ''}</p>
                                        </div>

                                        <div className="border-l-2 border-emerald-500 pl-4">
                                            <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Lead Tenant</p>
                                            <p className="font-medium text-lg">{formData.name}</p>
                                            <p className="text-gray-400 text-xs">{formData.email}</p>
                                        </div>

                                        <div className="border-l-2 border-emerald-500 pl-4">
                                            <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Tenancy Dates</p>
                                            <p className="font-medium text-lg">{formData.tenancyStartDate ? new Date(formData.tenancyStartDate).toLocaleDateString() : 'N/A'}</p>
                                            <p className="text-gray-400 text-xs">to {formData.tenancyEndDate ? new Date(formData.tenancyEndDate).toLocaleDateString() : 'Rolling'}</p>
                                        </div>

                                        <div className="border-l-2 border-emerald-500 pl-4">
                                            <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Financials</p>
                                            <p className="font-medium text-lg">£{formData.rentAmount} <span className="text-xs text-gray-400">/ {formData.rentFrequency}</span></p>
                                            <p className="text-gray-400 text-xs">Deposit: £{formData.depositAmount || '0'} ({formData.depositScheme || 'No Scheme'})</p>
                                        </div>

                                        <div className="border-l-2 border-emerald-500 pl-4">
                                            <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Rent Payment</p>
                                            <p className="font-medium text-lg">Day {formData.rentPaymentDay || '—'} of each month</p>
                                            <p className="text-gray-400 text-xs">{formData.freePeriodGranted ? `${formData.freePeriodDays || 0} free days granted` : 'No free period'}</p>
                                        </div>

                                        <div className="col-span-2 mt-4 pt-6 border-t border-gray-800 flex items-center justify-between">
                                            <div>
                                                <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Compliance Status</p>
                                                <div className="flex items-center">
                                                    <i className={`fas ${formData.complianceException ? 'fa-shield-virus text-amber-400' : 'fa-shield-check text-emerald-400'} mr-2`}></i>
                                                    <span className="font-bold uppercase">{formData.complianceException ? 'Exception Applied' : 'Compliant'}</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-emerald-400 font-bold uppercase tracking-widest text-[10px] mb-1">Agreement</p>
                                                <p className="font-medium">{agreements.find(a => a.id === formData.agreementId)?.title || 'No Template Linked'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 p-4 bg-white bg-opacity-5 rounded-xl text-[11px] text-gray-400 leading-relaxed italic">
                                        By creating this tenancy, you confirm that all required legal documents have been served or are scheduled for automated delivery through the portal. This record will be immutable in the Court Evidence Pack.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-between mt-8 pt-4 border-t">
                        <button onClick={step === 'PROPERTY' ? onClose : handleBack} className="px-6 py-2 bg-gray-100 rounded hover:bg-gray-200">
                            {step === 'PROPERTY' ? 'Cancel' : 'Back'}
                        </button>

                        {step === 'REVIEW' ? (
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="px-6 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {loading ? 'Creating...' : 'Create Tenancy'}
                            </button>
                        ) : (() => {
                            // Compute whether Next is allowed based on current step state.
                            // On Step 5 (COMPLIANCE) with blockers, the officer must
                            // tick the exception AND provide a comment AND a future
                            // remediation date before Next becomes active.
                            const blockedCompliance = step === 'COMPLIANCE'
                                && complianceResult
                                && !complianceResult.canProceed;
                            const exceptionIncomplete = blockedCompliance && (
                                !formData.complianceException
                                || !formData.complianceExceptionReason?.trim()
                                || !formData.complianceExceptionRemediationDate
                            );
                            const propertyBlocked = step === 'PROPERTY'
                                && (!formData.propertyId || capacityState?.isFullyOccupied);
                            const disabled = checking || exceptionIncomplete || propertyBlocked;

                            return (
                                <button
                                    onClick={handleNext}
                                    disabled={disabled}
                                    className="px-6 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={
                                        exceptionIncomplete ? 'Tick the exception checkbox and provide officer comment + remediation date to proceed.'
                                        : propertyBlocked ? (capacityState?.isFullyOccupied ? 'Property is fully occupied.' : 'Select a property.')
                                        : ''
                                    }
                                >
                                    {checking ? 'Checking...' : 'Next'}
                                </button>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
}
